import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Globe, TrendingUp, Award, CheckCircle2, ChevronDown } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';

const Home = () => {
  const heroRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState('default');
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);
  
  // Smooth spring animations
  const springConfig = { stiffness: 100, damping: 30 };
  const cursorX = useSpring(useMotionValue(0), springConfig);
  const cursorY = useSpring(useMotionValue(0), springConfig);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      cursorX.set(e.clientX - 20);
      cursorY.set(e.clientY - 20);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cursorX, cursorY]);

  const services = [
    {
      icon: <Globe className="w-12 h-12" />,
      title: 'Relocation & Expatriation',
      description: 'Seamless relocation services for individuals and families moving to the UAE.',
      color: '#3498DB',
      gradient: 'from-[#3498DB] to-[#2980B9]'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Business Setup',
      description: 'Complete business formation and registration services in Dubai and UAE.',
      color: '#27AE60',
      gradient: 'from-[#27AE60] to-[#1E8449]'
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Financial Consulting',
      description: 'Expert financial advisory and tax consulting for businesses and individuals.',
      color: '#5DADE2',
      gradient: 'from-[#5DADE2] to-[#3498DB]'
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: 'Corporate Services',
      description: 'Comprehensive corporate support including compliance and governance.',
      color: '#D4AF37',
      gradient: 'from-[#D4AF37] to-[#B8860B]'
    },
  ];

  const benefits = [
    'Expert team with years of UAE experience',
    '360° comprehensive service approach',
    'Personalized solutions for your needs',
    'Fast and efficient processing',
    'Transparent pricing and timelines',
    'Post-setup support and guidance',
  ];

  const teamMembers = [
    { name: 'Sergio Morgado', role: 'CEO', image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/wkjilbx9_image.png' },
    { name: 'Mouna Kallel', role: 'CFO', image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/fzwztwq6_image.png' },
    { name: 'José Canto Moreira', role: 'Director', image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/mwjouotn_image.png' },
  ];

  const stats = [
    { value: '15+', label: 'Years in Business', color: '#5DADE2' },
    { value: '12', label: 'Specialists', color: '#27AE60' },
    { value: '100%', label: 'Satisfaction', color: '#5DADE2' },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] } }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#0a0a0a]" data-testid="home-page">
      {/* Custom Cursor */}
      <motion.div
        className="fixed w-10 h-10 rounded-full pointer-events-none z-[9999] mix-blend-difference hidden md:block"
        style={{
          x: cursorX,
          y: cursorY,
          background: cursorVariant === 'button' 
            ? 'rgba(52, 152, 219, 0.8)' 
            : 'rgba(255, 255, 255, 0.8)',
          scale: cursorVariant === 'button' ? 1.5 : 1,
        }}
        transition={{ type: "spring", stiffness: 500, damping: 28 }}
      />

      {/* Hero Section with Video Background */}
      <section
        ref={heroRef}
        className="relative h-screen flex items-center justify-center text-white overflow-hidden"
        data-testid="hero-section"
      >
        {/* Video Background */}
        <motion.div
          style={{ scale: heroScale }}
          className="absolute inset-0 w-full h-full"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920"
          >
            <source src="https://videos.pexels.com/video-files/4328452/4328452-uhd_2560_1440_25fps.mp4" type="video/mp4" />
          </video>
        </motion.div>
        
        {/* Animated Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <motion.div 
          className="absolute inset-0 opacity-40"
          animate={{
            background: [
              'radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(39, 174, 96, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 50% 50%, rgba(93, 173, 226, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 20% 80%, rgba(52, 152, 219, 0.3) 0%, transparent 50%)',
            ]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Animated Lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"
              style={{ top: `${20 + i * 15}%`, left: 0, right: 0 }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 8, delay: i * 0.5, repeat: Infinity, ease: "linear" }}
            />
          ))}
        </div>
        
        {/* Content */}
        <motion.div
          style={{ opacity: heroOpacity, y: heroY }}
          className="relative z-10 max-w-7xl mx-auto px-6 text-center"
        >
          <motion.h1 
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[1.1]"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Your Gateway to
              </motion.span>
            </span>
            <span className="block overflow-hidden mt-2">
              <motion.span
                className="block bg-gradient-to-r from-[#3498DB] via-[#5DADE2] to-[#27AE60] bg-clip-text text-transparent"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Business Success
              </motion.span>
            </span>
            <span className="block overflow-hidden mt-2">
              <motion.span
                className="block"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
              >
                in the UAE
              </motion.span>
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.25, 0.1, 0.25, 1] }}
            data-testid="hero-subheadline"
          >
            Expert consulting services for entrepreneurs, investors, and expatriates in Dubai.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <Link 
              to="/contact" 
              className="group relative overflow-hidden px-10 py-5 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-bold text-lg rounded-full transition-all duration-500 hover:shadow-[0_0_60px_rgba(52,152,219,0.5)]"
              onMouseEnter={() => setCursorVariant('button')}
              onMouseLeave={() => setCursorVariant('default')}
              data-testid="hero-cta-button"
            >
              <span className="relative z-10 flex items-center gap-3">
                Book Free Consultation
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight size={22} />
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#27AE60] to-[#3498DB]"
                initial={{ x: '100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.4 }}
              />
            </Link>
            
            <Link 
              to="/services"
              className="group flex items-center gap-3 px-8 py-5 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 backdrop-blur-sm"
              onMouseEnter={() => setCursorVariant('button')}
              onMouseLeave={() => setCursorVariant('default')}
            >
              Explore Services
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
            </Link>
          </motion.div>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <motion.div
            className="flex flex-col items-center gap-3 cursor-pointer"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-white/60">Scroll</span>
            <div className="w-[1px] h-16 bg-gradient-to-b from-white/60 to-transparent" />
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section - Horizontal Scroll Style */}
      <section className="py-32 bg-[#0a0a0a] relative" data-testid="services-overview-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(52,152,219,0.05)_0%,_transparent_70%)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="mb-20"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-6">
              <div className="w-20 h-[2px] bg-gradient-to-r from-[#3498DB] to-transparent" />
              <span className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">Services</span>
            </motion.div>
            
            <motion.h2 
              variants={itemVariants}
              className="text-5xl md:text-7xl font-bold text-white mb-6"
              data-testid="services-heading"
            >
              What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Offer</span>
            </motion.h2>
            
            <motion.p 
              variants={itemVariants}
              className="text-xl text-gray-400 max-w-2xl"
            >
              Comprehensive solutions tailored to your business and relocation needs in the UAE
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative"
                onMouseEnter={() => setCursorVariant('button')}
                onMouseLeave={() => setCursorVariant('default')}
                data-testid={`service-card-${index}`}
              >
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-10 h-full transition-all duration-700 group-hover:border-white/20 group-hover:bg-white/[0.08]">
                  {/* Hover Gradient */}
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-700`}
                  />
                  
                  {/* Animated Corner */}
                  <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden">
                    <motion.div
                      className="absolute top-0 right-0 w-full h-full"
                      style={{ background: `linear-gradient(135deg, ${service.color}20, transparent)` }}
                      initial={{ x: '100%', y: '-100%' }}
                      whileHover={{ x: 0, y: 0 }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  
                  <div className="relative z-10">
                    <motion.div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8 transition-all duration-500"
                      style={{ background: `${service.color}15`, color: service.color }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      {service.icon}
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-all duration-500">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-400 leading-relaxed mb-8 group-hover:text-gray-300 transition-colors duration-500">
                      {service.description}
                    </p>
                    
                    <Link
                      to="/services"
                      className="flex items-center gap-2 font-semibold hover:gap-4 transition-all duration-300"
                      style={{ color: service.color }}
                    >
                      Learn More
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                  
                  {/* Bottom Line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r"
                    style={{ background: `linear-gradient(90deg, ${service.color}, transparent)` }}
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <Link 
              to="/services" 
              className="group inline-flex items-center gap-4 px-10 py-5 border border-white/20 text-white font-semibold rounded-full hover:bg-white hover:text-[#0a0a0a] transition-all duration-500"
              onMouseEnter={() => setCursorVariant('button')}
              onMouseLeave={() => setCursorVariant('default')}
              data-testid="view-all-services-button"
            >
              View All Services
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section - Split Screen */}
      <section className="py-32 bg-[#0a0a0a] relative overflow-hidden" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInLeft}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-[2px] bg-gradient-to-r from-[#27AE60] to-transparent" />
                <span className="text-[#27AE60] font-semibold tracking-[0.2em] uppercase text-sm">Why Us</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-8" data-testid="benefits-heading">
                Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#27AE60] to-[#3498DB]">SAM</span>?
              </h2>
              
              <p className="text-xl text-gray-400 mb-12 leading-relaxed">
                We provide end-to-end solutions with a 360° approach, ensuring your success in the UAE market.
              </p>
              
              <motion.ul 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
                className="space-y-6"
              >
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index} 
                    variants={itemVariants}
                    className="flex items-center gap-5 group cursor-pointer"
                    data-testid={`benefit-item-${index}`}
                    onMouseEnter={() => setCursorVariant('button')}
                    onMouseLeave={() => setCursorVariant('default')}
                  >
                    <motion.div 
                      className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#27AE60] to-[#3498DB] flex items-center justify-center flex-shrink-0"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                    >
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </motion.div>
                    <span className="text-gray-300 text-lg group-hover:text-white transition-colors duration-300">{benefit}</span>
                  </motion.li>
                ))}
              </motion.ul>
              
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-12"
              >
                <Link 
                  to="/why-choose-us" 
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-[#0a0a0a] font-bold rounded-full hover:bg-gradient-to-r hover:from-[#27AE60] hover:to-[#3498DB] hover:text-white transition-all duration-500"
                  onMouseEnter={() => setCursorVariant('button')}
                  onMouseLeave={() => setCursorVariant('default')}
                  data-testid="learn-more-button"
                >
                  Learn More
                  <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInRight}
              className="relative"
            >
              <div className="relative group">
                {/* Main Image */}
                <motion.div
                  className="relative overflow-hidden rounded-3xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.6 }}
                >
                  <img
                    src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800"
                    alt="Dubai Skyline"
                    className="w-full h-[600px] object-cover"
                    data-testid="benefits-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                </motion.div>
                
                {/* Floating Stats Card */}
                <motion.div
                  className="absolute -bottom-10 -left-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">15+</div>
                  <div className="text-gray-400 text-sm">Years of Experience</div>
                </motion.div>
                
                {/* Decorative Elements */}
                <motion.div
                  className="absolute -top-8 -right-8 w-40 h-40 rounded-3xl border border-[#3498DB]/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-32 bg-gradient-to-b from-[#0a0a0a] to-[#111]" data-testid="team-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-20"
          >
            <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[2px] bg-gradient-to-l from-[#5DADE2] to-transparent" />
              <span className="text-[#5DADE2] font-semibold tracking-[0.2em] uppercase text-sm">Our Team</span>
              <div className="w-20 h-[2px] bg-gradient-to-r from-[#5DADE2] to-transparent" />
            </motion.div>
            
            <motion.h2 
              variants={itemVariants}
              className="text-5xl md:text-7xl font-bold text-white mb-6"
              data-testid="team-heading"
            >
              Meet Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5DADE2] to-[#27AE60]">Leadership</span>
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative"
                onMouseEnter={() => setCursorVariant('button')}
                onMouseLeave={() => setCursorVariant('default')}
                data-testid={`team-member-card-${index}`}
              >
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent">
                  <motion.div
                    className="relative overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }}
                  >
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-[500px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
                    
                    {/* Hover Overlay */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-t from-[#3498DB]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </motion.div>
                  
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <motion.h3 
                      className="text-2xl font-bold text-white mb-2"
                      initial={{ y: 20, opacity: 0.8 }}
                      whileHover={{ y: 0, opacity: 1 }}
                    >
                      {member.name}
                    </motion.h3>
                    <p className="text-[#5DADE2] font-medium text-lg">{member.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-16"
          >
            <Link 
              to="/team" 
              className="group inline-flex items-center gap-4 px-10 py-5 border border-white/20 text-white font-semibold rounded-full hover:bg-white hover:text-[#0a0a0a] transition-all duration-500"
              onMouseEnter={() => setCursorVariant('button')}
              onMouseLeave={() => setCursorVariant('default')}
              data-testid="meet-full-team-button"
            >
              Meet the Full Team
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section - Cinematic */}
      <section className="py-32 relative overflow-hidden" data-testid="stats-section">
        {/* Background Video/Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1920" 
            alt="Dubai Night"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/80" />
        </div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '100px 100px'
          }} />
        </div>
        
        <div className="max-w-5xl mx-auto px-6 relative">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-3 gap-12 justify-center"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center group"
                data-testid={`stat-${index}`}
                onMouseEnter={() => setCursorVariant('button')}
                onMouseLeave={() => setCursorVariant('default')}
              >
                <motion.div 
                  className="text-6xl md:text-8xl font-bold mb-4"
                  style={{ color: stat.color }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-400 text-lg font-light tracking-wider">{stat.label}</div>
                
                {/* Underline */}
                <motion.div
                  className="w-0 h-[2px] mx-auto mt-4 group-hover:w-20 transition-all duration-500"
                  style={{ background: stat.color }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-[#0a0a0a]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-20"
          >
            <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[2px] bg-gradient-to-l from-[#D4AF37] to-transparent" />
              <span className="text-[#D4AF37] font-semibold tracking-[0.2em] uppercase text-sm">Testimonials</span>
              <div className="w-20 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent" />
            </motion.div>
            
            <motion.h2 
              variants={itemVariants}
              className="text-5xl md:text-7xl font-bold text-white"
              data-testid="testimonials-heading"
            >
              They Trust <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F4D03F]">Us</span>
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                quote: "SAM Relocation Dubai helped me setup my company very easily. What I appreciated the most is that they took time to listen and understand all business requirements.",
                author: "Ilyess Zenasni",
                company: "CEO Quacky Studio"
              },
              {
                quote: "Best team in Dubai if you want to start your business quickly! Serious and very professional. L'équipe se montre en permanence de très bon conseils.",
                author: "Brice Martin",
                company: "CEO INA Trading"
              },
              {
                quote: "I'm very happy to work with SAM Relocation Dubai. They are very accurate, fast and do everything at the highest level. Like the best Swiss watches!",
                author: "George Mgaloblichvili",
                company: "CEO Gaztrade FZCO"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative"
                onMouseEnter={() => setCursorVariant('button')}
                onMouseLeave={() => setCursorVariant('default')}
                data-testid={`testimonial-${index}`}
              >
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-10 h-full transition-all duration-700 group-hover:border-[#D4AF37]/30">
                  <div className="text-8xl font-serif text-[#D4AF37]/20 absolute -top-4 -left-2">"</div>
                  
                  <p className="text-gray-300 leading-relaxed text-lg relative z-10 mb-8">{testimonial.quote}</p>
                  
                  <div className="border-t border-white/10 pt-6">
                    <p className="font-bold text-white text-lg">{testimonial.author}</p>
                    <p className="text-[#D4AF37]">{testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What If Section */}
      <section className="py-32 bg-gradient-to-b from-[#0a0a0a] to-[#111]" data-testid="what-if-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-20"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-5xl md:text-7xl font-bold text-white"
            >
              What <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] via-[#27AE60] to-[#5DADE2]">if</span>?
            </motion.h2>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: <Building2 className="w-10 h-10" />,
                color: '#3498DB',
                title: 'Business development support?',
                description: 'SAM is an incubator for talented entrepreneurs needing support to structure a project step by step.',
                link: '/sam-incubator',
                linkText: 'SAM Incubator'
              },
              {
                icon: <Users className="w-10 h-10" />,
                color: '#27AE60',
                title: 'Experienced freelancer?',
                description: 'SAM brings together qualified professionals to propose ALL IN ONE activities.',
                link: '/sam-talents',
                linkText: 'SAM Talents'
              },
              {
                icon: <TrendingUp className="w-10 h-10" />,
                color: '#5DADE2',
                title: 'One stop solution?',
                description: 'SAM Solutions provides project management for relocation, marketing and events.',
                link: '/sam-solutions',
                linkText: 'SAM Solutions'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group"
                onMouseEnter={() => setCursorVariant('button')}
                onMouseLeave={() => setCursorVariant('default')}
                data-testid={`what-if-${index}`}
              >
                <Link to={item.link} className="block">
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10 h-full transition-all duration-700 group-hover:border-white/30 group-hover:transform group-hover:-translate-y-2">
                    <motion.div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8"
                      style={{ background: `${item.color}15`, color: item.color }}
                      whileHover={{ scale: 1.1, rotate: 10 }}
                    >
                      {item.icon}
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {item.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-8 leading-relaxed">
                      {item.description}
                    </p>
                    
                    <div 
                      className="flex items-center gap-2 font-bold text-lg transition-all duration-300 group-hover:gap-4"
                      style={{ color: item.color }}
                    >
                      {item.linkText}
                      <ArrowRight size={20} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-40 relative overflow-hidden" data-testid="final-cta-section">
        {/* Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="https://videos.pexels.com/video-files/3769735/3769735-uhd_2560_1440_30fps.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#0a0a0a]/85" />
        </div>
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight" data-testid="final-cta-heading">
              Ready to Start Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] via-[#5DADE2] to-[#27AE60]">
                UAE Journey
              </span>?
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let our experts guide you through every step of your business setup or relocation process.
            </p>
            
            <Link 
              to="/contact" 
              className="group relative inline-flex items-center gap-4 px-14 py-6 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-bold text-xl rounded-full overflow-hidden transition-all duration-500 hover:shadow-[0_0_80px_rgba(52,152,219,0.5)]"
              onMouseEnter={() => setCursorVariant('button')}
              onMouseLeave={() => setCursorVariant('default')}
              data-testid="final-cta-button"
            >
              <span className="relative z-10">Schedule Your Free Consultation</span>
              <motion.span
                className="relative z-10"
                animate={{ x: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight size={24} />
              </motion.span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#27AE60] to-[#3498DB]"
                initial={{ x: '100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.4 }}
              />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
